import DateTimeFormatter from '@Components/DateTimeFormatter/DateTimeFormatter';
import MainTable from '@Components/MainTable';
import { MainTableRow } from '@Components/MainTable/MainTable';
import NoResults from '@Components/NoResults/NoResults';
import { DataCard, DataCardLabel, DataCardValue } from '@Components/UI/Cards/DataCard/DataCard';
import { TableHeader } from '@Features/generics/generics.type';
import { fetchTestAttemptsDropdown } from '@Features/dropdowns/dropdownsSlice';
import { useTypedSelector } from '@Features/store';
import AttendanceCount from '@Pages/Students/StudentTabs/ClassRooms/Pages/Components/Attendances/Components/AttendanceCount';
import TestsCounts from '@Pages/Students/StudentTabs/ClassRooms/Pages/Components/Tests/Components/TestsCounts';
import TestSummaryModel from '@Pages/Students/StudentTabs/ClassRooms/Pages/Components/Tests/Components/TestSummaryModel/TestSummaryModel';
import { clearContentApiScope, setContentApiScope } from '@ContentManagement/ContentModeContext';
import { useSnackbar } from '@Providers/useSnackbar';
import api from '@Services/api';
import { formatDuration } from '@Utils/formatDuration';
import { TableCell, Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import RoundButton from '@Components/UI/Buttons/RoundButton';
import EdIcon from '@Components/UI/Utilities/EdIcon/EdIcon';
import { FlexLayout } from '@Styled/utilities';
import {
	apiData,
	errorMessage,
	progressColor,
	useSubscriptionGrants,
} from '../Subscriptions/subscriptions.shared';
import useStudentList from '../Subscriptions/useStudentList';
import type { ReportTarget } from './PerformanceReport';

/** GETs one of the report's count endpoints (`…/performance/<kind>/count`). */
const useReportCount = <T,>(target: ReportTarget, kind: 'attendance' | 'tests' | 'webinars') => {
	const { displaySnackbar } = useSnackbar();
	const [count, setCount] = useState<T>();
	useEffect(() => {
		let cancelled = false;
		api
			.post(`user/profile/${target.studentId}/performance/${kind}/count`, { type: target.type, id: target.id })
			.then((response) => !cancelled && setCount(apiData<T>(response)))
			.catch((error) => !cancelled && displaySnackbar('error', errorMessage(error, `Unable to load ${kind} counts`)));
		return () => {
			cancelled = true;
		};
	}, [target.studentId, target.type, target.id, kind, displaySnackbar]);
	return count;
};

const useReportTable = <T,>(target: ReportTarget, kind: 'attendance' | 'tests' | 'webinars') =>
	useStudentList<T>(target.studentId, `performance/${kind}`, [], `Unable to load ${kind}`, {
		type: target.type,
		id: target.id,
	});

/** Export button above a report table (classroom report placement). */
const ExportBar: React.FC<{ onExport: () => void; exporting: boolean }> = ({ onExport, exporting }) => {
	const { canExport } = useSubscriptionGrants();
	if (!canExport) return null;
	return (
		<FlexLayout justifyContent="flex-end" marginBottom="1rem">
			<ExportButton
				onClick={onExport}
				disabled={exporting}
				startIcon={<EdIcon>south</EdIcon>}
				variant="contained"
			>
				Export
			</ExportButton>
		</FlexLayout>
	);
};

const emptyRow = (colSpan: number) => (
	<MainTableRow>
		<TableCell colSpan={colSpan}>
			<NoResults />
		</TableCell>
	</MainTableRow>
);

// ── Attendance ─────────────────────────────────────────────────────────────

const ATTENDANCE_HEADS: TableHeader[] = [
	{ displayName: 'Lecture', fieldName: 'name', width: '20%' },
	{ displayName: 'Course', fieldName: 'course', canSort: false },
	{ displayName: 'Section', fieldName: 'section_name', canSort: false },
	{ displayName: 'State', fieldName: 'state', canSort: false },
	{ displayName: 'Expiry', fieldName: 'expire_at', canSort: false },
	{ displayName: 'St.Status', fieldName: 'student_status', canSort: false },
	{ displayName: 'Progress', fieldName: 'progress', canSort: false },
	{ displayName: 'Time', fieldName: 'time', canSort: false },
];

export const PerfAttendance: React.FC<{ target: ReportTarget }> = ({ target }) => {
	const count = useReportCount<any>(target, 'attendance');
	const { rows, meta, load, filterOptions, exportCsv, exporting } = useReportTable<any>(target, 'attendance');
	return (
		<>
			{count && <AttendanceCount count={count} />}
			<ExportBar onExport={exportCsv} exporting={exporting} />
			<MainTable
				onPageChangeRequest={load}
				tableFilters={filterOptions}
				tableHeads={ATTENDANCE_HEADS}
				totalPages={Number(meta.last_page || 1)}
				total={Number(meta.total || 0)}
				RowsperPage={Number(meta.per_page || 10)}
				pageNum={Number(meta.current_page || 1)}
				renderItems={() =>
					rows.length === 0
						? emptyRow(ATTENDANCE_HEADS.length)
						: rows.map((row) => {
								const enrollment = row.enrollment;
								const parent = row.standalone_course ?? row.standaloneCourse;
								return (
									<MainTableRow key={row.id} hover>
										<Tooltip title={row.name} placement="top">
											<TableCell>{row.name}</TableCell>
										</Tooltip>
										<TableCell>{parent?.title ?? '-'}</TableCell>
										<TableCell>{row.section?.name ?? '-'}</TableCell>
										<TableCell>{!enrollment ? '-' : enrollment.expired ? 'expired' : 'active'}</TableCell>
										<TableCell>{enrollment?.expire_at ? <DateTimeFormatter date={enrollment.expire_at} /> : '-'}</TableCell>
										<TableCell>
											{!enrollment ? (
												<Status color={progressColor('not started')}>not started</Status>
											) : (
												<Status color={progressColor(enrollment.progress_status)}>{enrollment.progress_status}</Status>
											)}
										</TableCell>
										<TableCell>{enrollment ? `${enrollment.progress}%` : '-'}</TableCell>
										<TableCell>{Number(row.time) > 0 ? formatDuration(Number(row.time)) : '-'}</TableCell>
									</MainTableRow>
								);
						  })
				}
			/>
		</>
	);
};

// ── Tests ──────────────────────────────────────────────────────────────────

const TEST_HEADS: TableHeader[] = [
	{ displayName: 'Lecture', fieldName: 'course_name', canSort: false },
	{ displayName: 'Test Name', fieldName: 'test_name', canSort: false },
	{ displayName: 'Attempt. Num', fieldName: 'attempts_count', canSort: false },
	{ displayName: 'Score', fieldName: 'score', canSort: false },
	{ displayName: 'Status', fieldName: 'status', canSort: false },
	{ displayName: 'Attempt Date', fieldName: 'attempt_date', canSort: false },
];

const testStatusColor = (status: string) => {
	switch (status) {
		case 'passed':
			return '#00d66b';
		case 'failed':
			return '#ff3100';
		case 'review':
			return '#ffab00';
		default:
			return '#a4a4a4';
	}
};

export const PerfTests: React.FC<{ target: ReportTarget }> = ({ target }) => {
	const dispatch = useDispatch();
	const count = useReportCount<any>(target, 'tests');
	const { rows, meta, load, filterOptions, exportCsv, exporting } = useReportTable<any>(target, 'tests');
	const { currentEntity: student } = useTypedSelector((state) => state.Students);
	const attempts = useTypedSelector((state) => state.dropdowns.dropdownTestAttempts);
	const [summaryTest, setSummaryTest] = useState<any>();
	const [showSummary, setShowSummary] = useState(false);

	useEffect(() => {
		if (summaryTest?.unit_id && student?.id) {
			dispatch(fetchTestAttemptsDropdown({ unitId: summaryTest.unit_id, userId: student.id }));
		}
	}, [summaryTest?.unit_id, student?.id]);

	// The shared summary modal calls the classroom `test/*` routes, which refuse
	// standalone content. While it is open, route through the lecture scope
	// (same mechanism as the lecture management page); set synchronously on
	// open so the modal's first request is already scoped.
	const summaryLectureId = Number(summaryTest?.course_id) || undefined;
	const openSummary = (test: any) => {
		if (test.course_id) setContentApiScope({ mode: 'standalone-lecture', lectureId: Number(test.course_id) });
		setSummaryTest(test);
		setShowSummary(true);
	};
	useEffect(() => {
		if (!showSummary || !summaryLectureId) return;
		return () => clearContentApiScope(summaryLectureId);
	}, [showSummary, summaryLectureId]);

	return (
		<>
			{summaryTest && (
				<TestSummaryModel
					handleModelClose={setShowSummary}
					openModel={showSummary}
					user={student}
					testInfo={summaryTest}
					attempts={attempts}
					setUserId={setSummaryTest}
				/>
			)}
			{count && <TestsCounts count={count} />}
			<ExportBar onExport={exportCsv} exporting={exporting} />
			<MainTable
				onPageChangeRequest={load}
				tableFilters={filterOptions}
				tableHeads={TEST_HEADS}
				totalPages={Number(meta.last_page || 1)}
				total={Number(meta.total || 0)}
				RowsperPage={Number(meta.per_page || 10)}
				pageNum={Number(meta.current_page || 1)}
				renderItems={() =>
					rows.length === 0
						? emptyRow(TEST_HEADS.length)
						: rows.map((test) => (
								<MainTableRow
									key={test.test_id}
									hover
									sx={{ cursor: test.attempt_id ? 'pointer' : 'default' }}
									onClick={() => {
										if (!test.attempt_id) return;
										openSummary(test);
									}}
								>
									<TableCell>{test.course_name}</TableCell>
									<TableCell>{test.test_name}</TableCell>
									<TableCell>{test.attempts_count}</TableCell>
									<TableCell>
										{test.score === null || test.score === undefined ? '-' : `${test.score} / ${test.overall_score}`}
									</TableCell>
									<TableCell>
										<Status color={testStatusColor(test.status)}>{test.status}</Status>
									</TableCell>
									<TableCell>{test.attempt_date ? <DateTimeFormatter date={test.attempt_date} /> : '-'}</TableCell>
								</MainTableRow>
						  ))
				}
			/>
		</>
	);
};

// ── Webinars ───────────────────────────────────────────────────────────────

const WEBINAR_HEADS: TableHeader[] = [
	{ displayName: 'Lecture', fieldName: 'course_name', canSort: false },
	{ displayName: 'Webinar', fieldName: 'webinar_name', canSort: false },
	{ displayName: 'Date', fieldName: 'slot_date', canSort: false },
	{ displayName: 'Duration', fieldName: 'duration', canSort: false },
	{ displayName: 'Status', fieldName: 'status', canSort: false },
];

export const PerfWebinars: React.FC<{ target: ReportTarget }> = ({ target }) => {
	const count = useReportCount<any>(target, 'webinars');
	const { rows, meta, load, filterOptions, exportCsv, exporting } = useReportTable<any>(target, 'webinars');
	return (
		<>
			{count && (
				<Cards>
					<Card color="primary">
						<CardLabel>Registered</CardLabel>
						<CardValue>{count.registered}</CardValue>
					</Card>
					<Card color="malachite">
						<CardLabel>Attended</CardLabel>
						<CardValue>{count.attended}</CardValue>
					</Card>
					<Card color="error">
						<CardLabel>Not attended</CardLabel>
						<CardValue>{count.not_attended}</CardValue>
					</Card>
					<Card color="warning">
						<CardLabel>Not Started</CardLabel>
						<CardValue>{count.not_started}</CardValue>
					</Card>
				</Cards>
			)}
			<ExportBar onExport={exportCsv} exporting={exporting} />
			<MainTable
				onPageChangeRequest={load}
				tableFilters={filterOptions}
				tableHeads={WEBINAR_HEADS}
				totalPages={Number(meta.last_page || 1)}
				total={Number(meta.total || 0)}
				RowsperPage={Number(meta.per_page || 10)}
				pageNum={Number(meta.current_page || 1)}
				renderItems={() =>
					rows.length === 0
						? emptyRow(WEBINAR_HEADS.length)
						: rows.map((webinar) => (
								<MainTableRow key={webinar.webinar_id} hover>
									<TableCell>{webinar.course_name}</TableCell>
									<TableCell>{webinar.webinar_name}</TableCell>
									<TableCell>{webinar.slot_date ? <DateTimeFormatter date={webinar.slot_date} /> : '-'}</TableCell>
									<TableCell>{webinar.duration ?? '-'}</TableCell>
									<TableCell>{webinar.status ?? '-'}</TableCell>
								</MainTableRow>
						  ))
				}
			/>
		</>
	);
};

const ExportButton = styled(RoundButton)`
	box-shadow: 0 5px 6px 0 rgba(0, 0, 0, 0.38);
`;
const Status = styled.div<{ color: string }>`
	color: ${({ color }) => color};
	font-weight: 500;
	text-transform: capitalize;
`;
const Cards = styled.div`
	display: flex;
	gap: 1rem;
	flex-wrap: wrap;
	margin-bottom: 2rem;
`;
const Card = styled(DataCard)`
	flex: 1;
	min-width: 10rem;
	padding-top: 1.75rem;
	padding-bottom: 1.75rem;
`;
const CardLabel = styled(DataCardLabel)`
	font-size: 1.25rem;
	font-weight: bold;
`;
const CardValue = styled(DataCardValue)`
	font-size: 1.75rem;
	font-weight: 600;
`;
